import { createEffect, createSignal, on, Show, useContext } from 'solid-js';
import { Button } from '../../ui-components/button/button';
import { Modal } from '../../ui-components/modal/modal';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Text } from '../../ui-components/text/text';
import { CoursePath, PathCategory } from '../course-path/course-path';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { gql } from 'graphql-request';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Heading } from '../../ui-components/heading/heading';
import { ShowpadCodeSubmission } from '../showpad-code-submission/showpad-code-submission';
import { StyledLoadingContainer, StyledNoCoursesSection, StyledNoCoursesSectionInner } from './course-list-style';
import { Link } from '../../ui-components/link/link';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';

const GET_PATHS_QUERY = gql`
    query GetPathsAndItemsByAccess {
        getPathsAndItemsByAccess {
            categoryName,
            paths {
                id
                name
                curriculums {
                    id
                    name
                    courses {
                        courseId
                        name
                        description
                        progress
                        completedDate
                        coverImageUrl {
                            small
                        }
                        durationInSeconds
                        tags {
                            name
                        }
                        path {
                            id
                            name
                        }
                        curriculum {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`;

export const CourseList = () => {
    const {
        userState,
        localize,
        createCachedResource,
        contactUsPage,
    } = useContext(AppContext);
    
    const [pathsCategories, setPathsCategories] = createSignal<PathCategory[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [ showModal, setShowModal ] = createSignal(false);
    const [ closeBtnRef, setCloseBtnRef ] = createSignal<HTMLElement | undefined>();

    const [ pathCategoriesData, { refetch } ] = createCachedResource(GET_PATHS_QUERY, {}, true);

    const hasNoCode = () => userState.user && !userState.user.showpadCode;

    createEffect(() => {
        if (hasNoCode()) {
            setTimeout(() => {
                setShowModal(true);
            }, 3000);
        }
    });

    createEffect(
        on(
            () => userState.user?.showpadCode,
            () => {
                // When code changes, we need to refetch the data                
                setLoading(true);
                refetch();
            }
        )
    );

    createEffect(() => {
        if (pathCategoriesData() && pathCategoriesData().getPathsAndItemsByAccess) {
            const pathsDataToSet = pathCategoriesData().getPathsAndItemsByAccess;
            setPathsCategories(pathsDataToSet);

            console.log('data', pathsDataToSet);
            
            setLoading(false);
        }
    });        

    const closeAnnouncement = () => {
        setShowModal(false);
    };

    const keydownListener = (e: any) => {
        if (showModal() && e.key === 'Escape') {
            closeAnnouncement();
        }
    };

    createEffect(() => {
        window.addEventListener('keydown', keydownListener);

        if (closeBtnRef()) {
            closeBtnRef()?.focus();
        }
    });

    const NoAccessCodeModal = () => (
        <Modal visible={showModal()} onClose={() => closeAnnouncement()} size='small' darkenBackground={true}>
            <StyledVerticalSpace size={5} />
            <Text fontSize='large' fontStyle='bold'>
                {localize('no-code-modal-header', 'Get access to courses')}
            </Text>

            <Text fontSize='normal' splitParagraph={true}>
                {localize('no-code-modal-text', 'Please get in touch with us to get access to our e-learning catalog.')}
            </Text>

            <StyledFlexRow gap='2rem' justifyContent='center' alignItems='center'>
                <Button
                    label={localize('close-modal-button-text', 'Close')}
                    variant='secondary'
                    onClick={closeAnnouncement}
                    element={(el: HTMLElement) => {
                        setCloseBtnRef(el);
                    }}
                />

                <Button
                    label={localize('contact-us', 'Contact us')}
                    isLink={true}
                    url={contactUsPage.url}
                />
            </StyledFlexRow>

        </Modal>
    );

    const SubmitCodeSection = () => (
        <StyledNoCoursesSection>
            <Heading tag='h3' variant='bigRed'>
                {localize(
                    'no-courses-available',
                    'No courses available'
                )}
            </Heading>

            <StyledNoCoursesSectionInner>
                <div>
                    <Text fontSize='normal'>
                        {localize(
                            'provide-valid-access-code',
                            'You need a valid access code to get access to the courses. If you do not have one, please contact us.'
                        )}
                    </Text>

                    <Link label={contactUsPage.title} url={contactUsPage.url} />
                </div>

                <ShowpadCodeSubmission />
            </StyledNoCoursesSectionInner>

        </StyledNoCoursesSection>
    );

    const HasCodeButNoCourses = () => (
        <Show when={!loading() && (userState?.user?.showpadCode) && (!pathsCategories() || pathsCategories()?.length === 0)}>
            <SubmitCodeSection />
        </Show>
    );

    const HasCourses = () => (
        <Show when={!loading() && pathsCategories()?.length > 0}>
            <CoursePath pathsCategories={pathsCategories()} />
        </Show>
    );

    return (
        <>
            <StyledLoadingContainer>
                <Show when={loading()}>
                    <LoadingSpinner />
                </Show>
            </StyledLoadingContainer>

            <Show when={userState.user}>
                <HasCodeButNoCourses />
                <HasCourses />
            </Show>

            <Show when={!loading() && !userState.user}>
                <Heading tag='h3' variant='smallRed'>
                    {localize('login-to-view-courses', 'You need to login to view courses')}
                </Heading>
            </Show>
            
            <Show when={hasNoCode()}>
                <SubmitCodeSection />
                <NoAccessCodeModal />
            </Show>
        </>
    );
};

